<template>
  <section class="betting-criteria-table well">
    <header class="page-header">
      <h1>
        Equity versus various ranges
      </h1>
    </header>
    <p>Immediately profitable breakeven percentage (via pot odds of {{ state.potOdds }}): <strong>{{ breakevenPercentageViaPotOdds }}%</strong></p>
    <p>True breakeven percentage (via real odds of {{ state.realOddsLHS }}:1): <strong>{{ breakevenPercentageViaRealOdds }}%</strong></p>
    <p>
      Real odds:
      <input
        type="number"
        v-bind:value="state.realOddsLHS"
        v-on:input="$emit('setRealOddsLHS', $event.target.valueAsNumber)"
        min="0.1"
        step="0.1"
      >
      -to-1
      <em>(see <a href="#should-you-call-final-conclusion">further down</a> for how to estimate this)</em>
    </p>
    <toggle-help-button
      v-bind:helpAreaVisible="helpAreaVisible"
      v-on:toggleHelp="helpAreaVisible = !helpAreaVisible"
    />
    <section class="well help" v-if="helpAreaVisible">
      <iframe width="560" height="315" src="https://www.youtube.com/embed/HZSf9zFOTgs?rel=0&amp;showinfo=0" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
      <p>The table below is meant to help you "zoom out" and think about how your hand performs against many possible ranges that your opponent could have.</p>
      <p>Here's how it works:</p>
      <ul>
        <li>2:1 means "for every two value bets, your opponent has one bluff".</li>
        <li>TPTK means "they're only betting TPTK and better for value".</li>
        <li><strong>So the intersection of 2:1 and TPTK means that they're betting TPTK and better for value, and one bluff for every two value hands.</strong></li>
        <li>The numbers in the cells show your equity versus the corresponding range minus the required equity given the real odds. So if you have 35% equity but need 40%, the cell will show "-5%" and be shaded a corresponding light red color.</li>
      </ul>
      <p>Notes:</p>
      <ul>
        <li>How are bluff hands selected? They are added in the following order until enough combos are added to satisfy the value-to-bluff ratio: <em>nut flush draw, second nut flush draw, third nut flush draw, weak flush draw, open ended straight draw, double gutshot, weak pair, two overcards, gutshot, one overcard, air</em>. And on the river it goes <em>weak high card, king high, ace high, bottom pair, etc.</em> So if you start adding nut flush draws and the value-to-bluff ratio gets satisfied, it won't continue to add second nut flush draws.</li>
        <li>What does "n/a" mean? It means that there weren't enough bluffs available for the cell to be possible.</li>
        <li>There are some kinks that I plan on working out on paired and other non-standard boards.</li>
      </ul>
    </section>
    <table v-if="possibleBettingCriteria.length > 0" class="table">
      <thead>
        <tr>
          <th></th>
          <th></th>
          <td colspan="5"><em>Value-to-bluff ratio</em></td>
        </tr>
        <tr>
          <th></th>
          <th></th>
          <th>All value</th>
          <th>3:1</th>
          <th>2:1</th>
          <th>1:1</th>
          <th>1:2</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(bettingCriterion, i) in possibleBettingCriteria">
          <td v-if="i === 0" v-bind:rowspan="possibleBettingCriteria.length"><em>Value betting range</em></td>
          <th style="text-align: right;">{{ fiveCardHandIdToDisplayString(bettingCriterion.threeToOne.worstValueId) }}+</th>
          <td v-bind:class="getDiffClass(bettingCriterion.allValue.equity)" v-bind:title="getCellTitle(bettingCriterion.allValue.equity)">
            {{ getCellText(bettingCriterion.allValue.equity) }}
          </td>
          <td v-bind:class="getDiffClass(bettingCriterion.threeToOne.equity)" v-bind:title="getCellTitle(bettingCriterion.allValue.equity)">
            {{ getCellText(bettingCriterion.threeToOne.equity) }}
          </td>
          <td v-bind:class="getDiffClass(bettingCriterion.twoToOne.equity)" v-bind:title="getCellTitle(bettingCriterion.allValue.equity)">
            {{ getCellText(bettingCriterion.twoToOne.equity) }}
          </td>
          <td v-bind:class="getDiffClass(bettingCriterion.oneToOne.equity)" v-bind:title="getCellTitle(bettingCriterion.allValue.equity)">
            {{ getCellText(bettingCriterion.oneToOne.equity) }}
          </td>
          <td v-bind:class="getDiffClass(bettingCriterion.oneToTwo.equity)" v-bind:title="getCellTitle(bettingCriterion.allValue.equity)">
            {{ getCellText(bettingCriterion.oneToTwo.equity) }}
          </td>
        </tr>
      </tbody>
    </table>
    <hr>
    <simulate-button v-on:simulate="simulate()"/>
  </section>
</template>

<script>
import utilities from '@/services/utilities/utilities';
import ToggleHelpButton from '@/components/guided-analyses/help/toggle-help-button';
import SimulateButton from '@/components/controls/simulate-button';

export default {
  name: 'betting-criteria-table',
  components: {
    'toggle-help-button': ToggleHelpButton,
    'simulate-button': SimulateButton,
  },
  props: ['state', 'breakevenPercentageViaPotOdds', 'breakevenPercentageViaRealOdds'],
  data: function () {
    return {
      helpAreaVisible: false,
    };
  },
  computed: {
    possibleBettingCriteria: function () {
      let possibleBettingCriteria = [];

      if (!this.state.bettingCriteria[this.state.streetToCall].threeToOne) {
        return [];
      }

      this.state.bettingCriteria[this.state.streetToCall].allValue.forEach(function (allValueBettingCriterion, i) {
        if (allValueBettingCriterion.equity !== null) {
          possibleBettingCriteria.push({
            allValue: this.state.bettingCriteria[this.state.streetToCall].allValue[i],
            threeToOne: this.state.bettingCriteria[this.state.streetToCall].threeToOne[i],
            twoToOne: this.state.bettingCriteria[this.state.streetToCall].twoToOne[i],
            oneToOne: this.state.bettingCriteria[this.state.streetToCall].oneToOne[i],
            oneToTwo: this.state.bettingCriteria[this.state.streetToCall].oneToTwo[i],
          });
        }
      }, this);

      return possibleBettingCriteria;
    },
  },
  methods: {
    fiveCardHandIdToDisplayString: function (fiveCardHandId) {
      return this.state.players.find(0).states[1].fiveCardHands.find(fiveCardHandId).fullText;
    },
    getCellText: function (equity) {
      if (equity === null) {
        return 'n/a';
      }

      return (equity - this.breakevenPercentageViaRealOdds).roundToTwoDecimalPlaces() + '%';
    },
    getDiffClass: function (equity) {
      if (equity === null) {
        return;
      }

      return `diff-${Math.floor(equity - this.breakevenPercentageViaRealOdds)}`;
    },
    getCellTitle: function (equity) {
      if (equity === null) {
        return 'Not enough bluffs availalble for this to be possible.';
      }
    },
    simulate: function () {
      this.helpAreaVisible = true;
      this.$emit('simulate');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "src/assets/grid-square-shades";

  .betting-criteria-table {
    font-size: 18px;

    .btn-xs {
      margin-bottom: 10px;
    }

    .help {
      li {
        max-width: 800px;
      }
    }

    iframe {
      max-width: 100%;
    }

    td[colspan] {
      text-align: center;
    }

    thead th {
      text-align: center;
    }

    tbody td {
      text-align: center;
    }

    tbody td[rowspan] {
      vertical-align: middle;
      text-align: right;
    }

    thead tr:first-child th:first-child {
      width: 100px;
    }

    thead tr:first-child th:nth-child(2) {
      width: 150px;
    }

    th, td {
      border: none;
    }

    @media (max-width: 425px) {
      table {
        font-size: 12px;
        width: 100%;
      }

      thead, tbody {
        overflow-x: scroll;
      }
    }

    .simulate-button-container {
      font-size: 12px;
    }

    input[type="number"] {
      width: 75px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/guided-analyses/should-you-call/conclusions/betting-criteria-table.vue